import { Alert, Box, Button, Typography, useTheme } from '@mui/material';
import { FieldArray, type FieldArrayRenderProps, useFormikContext } from 'formik';
import { Add } from 'iconsax-react';
import { type FC, useContext, useEffect } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { ProductsByPlantFormikType, QuoteFormikType } from '@core/formik/quote';
import Enums from '@core/types/api/Enums';
import { randomUUID } from '@core/utils/uuid';
import type { ProductSectionProps } from '@web/components/ProductSection/ProductSection';

import { useDispatchCustomerMismatch } from '../../DispatchCustomerCheck/useDispatchCustomerMismatch';
import { useQuoteDrawerContext } from '../QuoteDrawer';
import LocationForm from './LocationForm';

interface ProductsByPlantSectionProps {
  sectionType: 'main' | 'additional';
  curProductsRef: ProductSectionProps['curProductsRef'];
  project: ProductSectionProps['project'];
  projectProductsByProductID: ProductSectionProps['projectProductsByProductID'];
}

const copyProductsToPlant =
  (arrayHelpers: FieldArrayRenderProps) =>
  (plants: ProductsByPlantFormikType[]): void => {
    plants.forEach((plant) => {
      arrayHelpers.push(plant);
    });
  };

const ProductsByPlantSection: FC<ProductsByPlantSectionProps> = ({
  sectionType,
  curProductsRef,
  project,
  projectProductsByProductID,
}) => {
  const { readOnly } = useQuoteDrawerContext();
  const theme = useTheme();
  const formikBag = useFormikContext<QuoteFormikType>();
  const { userInfo } = useContext(SlabContext);

  const fieldName = sectionType === 'main' ? 'mainProductsByPlants' : 'additionalProductsByPlants';
  const plants:
    | QuoteFormikType['mainProductsByPlants']
    | QuoteFormikType['additionalProductsByPlants'] =
    sectionType === 'main'
      ? formikBag.values.mainProductsByPlants
      : formikBag.values.additionalProductsByPlants;

  const isMultiLocationEnabled = userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagMultiLocationEnabled,
  ]);

  const mainPlant = formikBag.values.mainProductsByPlants[0];
  const additionalPlant = formikBag.values.additionalProductsByPlants[0];
  // When multi-location is disabled, sync the additional services location with the main products location
  useEffect(() => {
    if (
      !isMultiLocationEnabled &&
      sectionType === 'additional' &&
      additionalPlant !== undefined &&
      mainPlant?.plant?.id !== additionalPlant?.plant?.id
    ) {
      formikBag.setFieldValue(
        'additionalProductsByPlants.0.plant',
        mainPlant?.plant ?? { id: null, option: null },
      );
      formikBag.setFieldValue(
        'additionalProductsByPlants.0.plantDistance',
        mainPlant?.plantDistance ?? null,
      );
      formikBag.setFieldValue(
        'additionalProductsByPlants.0.plantDistanceUnit',
        mainPlant?.plantDistanceUnit ?? null,
      );
      formikBag.setFieldValue(
        'additionalProductsByPlants.0.plantDriveTime',
        mainPlant?.plantDriveTime ?? null,
      );
    }
  }, [isMultiLocationEnabled, sectionType, mainPlant?.plant?.id, additionalPlant?.plant?.id]);

  const {
    hasBlockingIssue: hasDispatchMismatch,
    missingSystems: missingDispatchSystems,
    removeUnlinkedProducts,
  } = useDispatchCustomerMismatch({ curProductsRef });
  const dispatchSystemLabel = missingDispatchSystems.join(', ');

  return (
    <FieldArray
      name={fieldName}
      render={(arrayHelpers) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {plants?.length === 0 && (
            <Typography variant='body2' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              No locations added yet. Click &quot;Add Location&quot; to get started.
            </Typography>
          )}

          {hasDispatchMismatch && (
            <Alert
              severity='warning'
              sx={{ mt: 2 }}
              action={
                <Button color='warning' size='small' onClick={removeUnlinkedProducts}>
                  REMOVE PRODUCTS
                </Button>
              }
            >
              Warning: Customer not in {dispatchSystemLabel}. Remove {dispatchSystemLabel} products
              from this quote, or create this customer in {dispatchSystemLabel} dispatch first.
            </Alert>
          )}

          {plants?.map((plantItem, index) => (
            <LocationForm
              key={plantItem.clientId}
              index={index}
              sectionType={sectionType}
              arrayHelpers={arrayHelpers}
              curProductsRef={curProductsRef}
              project={project}
              projectProductsByProductID={projectProductsByProductID}
              onCopyProductsToLocations={copyProductsToPlant(arrayHelpers)}
            />
          ))}

          {(isMultiLocationEnabled || plants.length === 0) && !readOnly && (
            <Button
              startIcon={<Add />}
              onClick={() =>
                arrayHelpers.push({
                  clientId: randomUUID(),
                  plant: { id: null, option: null },
                  plantDistance: null,
                  plantDistanceUnit: null,
                  plantDriveTime: null,
                  products: [],
                })
              }
              sx={{
                textTransform: 'none',
                color: theme.palette.SlabIndigo['700'],
                justifyContent: 'flex-start',
                alignSelf: 'flex-start',
              }}
            >
              Add Location
            </Button>
          )}
        </Box>
      )}
    />
  );
};

export default ProductsByPlantSection;
